import { useCallback, useContext, useEffect } from "react";
import { ApolloError } from "@apollo/client";
import { create, StoreApi, UseBoundStore } from "zustand";

import { BuilderActionInput } from "@src/types.generated";
import { useToast } from "@src/lib/hooks/useToast";
import { secondsBetweenDates, secondsSinceDate } from "@src/lib/utils/dates";
import { uuid4 } from "@src/lib/utils/strings";
import { ClientIdContext } from "@src/features/builders/shared/presence/ClientIdContext";

// ─── Constants ──────────────────────────────────────────────────────────

// How long after the first error we keep retrying at the fast cadence.
export const GIVE_UP_AFTER_SECONDS = 50;

// After the fast-retry window, keep retrying at this slower cadence
// instead of stopping: a queue that stops flushing while holding edits
// is silent data loss on the next reload (OC-779).
export const SLOW_RETRY_INTERVAL_SECONDS = 30;

// How long an in-flight mutation may gate the flush loop. Past this,
// re-dispatch even though `loading` is still true: the server applies
// actions by uuid idempotently so a duplicate send is a no-op, while a
// never-settling request would otherwise freeze the queue forever.
export const STUCK_INFLIGHT_SECONDS = 60;

// Minimum wait between flushes — gives a rapid burst of actions a
// chance to collect into one batch.
export const DEBOUNCE_SECONDS = 1;

// Max actions sent per flush. Larger batches mean fewer round-trips
// but bigger payloads; 10 is the established LibraryItem default.
export const MAX_PER_FLUSH = 10;

// ─── Pure helpers ───────────────────────────────────────────────────────

/**
 * Append `next` to the queue. If `shouldDedupe(existing, next)`
 * returns true for any existing item, that item is dropped — the
 * latest enqueue wins. Used to collapse rapid same-target updates
 * (e.g. name edits or, for the workflow canvas, drag-stops on the
 * same node).
 */
export const enqueueWithDedupe = <T>(
  queue: ReadonlyArray<T>,
  next: T,
  shouldDedupe?: (existing: T, next: T) => boolean,
): T[] => {
  if (!shouldDedupe) return [...queue, next];
  return [...queue.filter((existing) => !shouldDedupe(existing, next)), next];
};

/**
 * Drop items whose uuid appears in `appliedUuids`. Used after a
 * successful flush to evict the items the server confirmed it
 * applied, leaving the rest in the queue for the next flush.
 */
export const evictApplied = <T extends { uuid: string }>(
  queue: ReadonlyArray<T>,
  appliedUuids: ReadonlySet<string>,
): T[] => queue.filter((item) => !appliedUuids.has(item.uuid));

/**
 * True if we should attempt a flush despite a prior error. For the
 * first 50 seconds after the first error we retry at the driver's
 * fast cadence; after that we degrade to one attempt every
 * SLOW_RETRY_INTERVAL_SECONDS (measured against lastSyncAt, which
 * recordError/markFlushStart keep current) — never stopping outright,
 * so a transient failure can't strand queued edits until reload.
 * Resets when a flush succeeds (caller sets firstSyncErrorAt back
 * to null).
 */
export const shouldRetryFailedSync = (
  firstSyncErrorAt: Date | null | undefined,
  lastSyncAt: Date,
  now: Date = new Date(),
): boolean => {
  if (!firstSyncErrorAt) return true;
  if (secondsBetweenDates(firstSyncErrorAt, now) < GIVE_UP_AFTER_SECONDS)
    return true;
  return secondsBetweenDates(lastSyncAt, now) >= SLOW_RETRY_INTERVAL_SECONDS;
};

// ─── Apollo handler factory ─────────────────────────────────────────────

/**
 * Structural shape of every `applyBuilderActions` mutation response.
 * Generated mutations differ in name but the field shape is the same.
 */
type ApplyBuilderActionsResponse = {
  applyBuilderActions: {
    success: boolean;
    appliedBuilderActions?: Array<{ uuid: string }> | null;
  };
};

/**
 * Error codes for which retrying an identical batch can never succeed
 * (the referenced object is gone, or the user may not perform the
 * action). Retrying these forever poisons the queue: everything behind
 * the failing action stops saving while the editor keeps its diverged
 * state. Auth-related codes are deliberately excluded — after a
 * re-login the same batch can succeed.
 */
export const NON_RETRYABLE_SYNC_ERROR_CODES: ReadonlySet<string> = new Set([
  "OBJECT_NOT_FOUND",
  "OBJECT_DISCARDED",
  "OBJECT_SUBMITTED",
  "PERMISSION_DENIED",
  "MISSING_ACTION_PERMISSIONS",
  "RESOLVER_IS_ADMIN_ONLY",
]);

/** True when `error` carries a code retrying can never fix. */
export const isNonRetryableSyncError = (error: ApolloError): boolean =>
  error.graphQLErrors.some((e) => {
    const code = e.extensions?.code;
    return typeof code === "string" && NON_RETRYABLE_SYNC_ERROR_CODES.has(code);
  });

/**
 * Returns Apollo `onCompleted` / `onError` handlers that route success
 * / failure into the store's `recordSuccess` / `recordError` actions.
 * A failure with a non-retryable code routes to `onNonRetryable`
 * instead of `recordError`, so a poisoned action can't block the queue
 * forever; it returns whether it handled the failure. The handler gets
 * no batch context because this Apollo version invokes `onError` with
 * only the error — the caller identifies the failed batch from the
 * store's `inFlightUuids`, recorded at flush time. Spread the result
 * into a generated `useApplyXMutation` options object alongside any
 * consumer-specific options (e.g. refetchQueries).
 */
export const createSyncMutationHandlers = ({
  recordSuccess,
  recordError,
  onNonRetryable,
}: {
  recordSuccess: (appliedUuids: ReadonlySet<string>) => void;
  recordError: () => void;
  onNonRetryable?: (error: ApolloError) => boolean;
}): {
  onCompleted: (data: ApplyBuilderActionsResponse | null | undefined) => void;
  onError: (error: ApolloError) => void;
} => ({
  onCompleted: (data) => {
    const r = data?.applyBuilderActions;
    if (!r) return;
    if (r.success) {
      const applied = r.appliedBuilderActions ?? [];
      recordSuccess(new Set(applied.map((a) => a.uuid)));
    } else {
      recordError();
    }
  },
  onError: (error) => {
    if (
      onNonRetryable &&
      isNonRetryableSyncError(error) &&
      onNonRetryable(error)
    ) {
      return;
    }
    recordError();
  },
});

/**
 * Hook wrapper around `createSyncMutationHandlers` — reads the store's
 * recordSuccess / recordError / discard actions and returns Apollo
 * handlers ready to spread into a `useApplyXMutation` options object.
 * A discarded batch also surfaces an error toast: the edits are gone
 * from the queue, and the editor may be showing state the server never
 * accepted. `discardMessage` overrides the toast copy (null skips it, for
 * a failure the page already explains); `onDiscarded` lets the page pull
 * the server's state back instead of asking for a reload.
 */
export const useSyncMutationHandlers = <TItem extends { uuid: string }>(
  store: UseBoundStore<StoreApi<BuilderActionsSyncStore<TItem>>>,
  {
    discardMessage,
    onDiscarded,
  }: {
    discardMessage?: (error: ApolloError) => string | null;
    onDiscarded?: () => void;
  } = {},
): ReturnType<typeof createSyncMutationHandlers> => {
  const recordSuccess = store((s) => s.recordSuccess);
  const recordError = store((s) => s.recordError);
  const { addErrorToast } = useToast();
  const onNonRetryable = useCallback(
    (error: ApolloError): boolean => {
      const { inFlightUuids, queue, discard } = store.getState();
      if (inFlightUuids.size === 0) return false;
      // The server rolls the whole batch back, so healthy edits queued
      // behind the poison action are dropped with it. Log them so a lost
      // edit can be reconstructed from the console / RUM.
      console.error(
        "Discarding unsyncable builder actions",
        queue.filter((item) => inFlightUuids.has(item.uuid)),
      );
      discard(inFlightUuids);
      onDiscarded?.();
      const message = discardMessage
        ? discardMessage(error)
        : "Some recent changes couldn't be saved and were removed. Reload the page to get back in sync.";
      if (message) addErrorToast({ message });
      return true;
    },
    [store, addErrorToast, discardMessage, onDiscarded],
  );
  return createSyncMutationHandlers({
    recordSuccess,
    recordError,
    onNonRetryable,
  });
};

// ─── Store factory ──────────────────────────────────────────────────────

/**
 * Shared shape for any BuilderActions sync queue (workflow,
 * LibraryItem, future). The store owns the queue + sync timestamps
 * and exposes the standard mutations. Per-domain divergence (queue
 * item shape, dedupe predicate, mutation hook, transform) stays in
 * each consumer's hook layer.
 */
export type BuilderActionsSyncStore<TItem extends { uuid: string }> = {
  queue: TItem[];
  lastSyncAt: Date;
  firstSyncErrorAt: Date | null;
  lastSuccessfulSyncAt: Date | null;
  /** The uuids of the batch most recently handed to the mutation —
   * what a terminal `onError` should discard. */
  inFlightUuids: ReadonlySet<string>;
  // Start of the flush whose response hasn't been recorded yet, if any.
  inFlightSince: Date | null;
  enqueue: (item: TItem) => void;
  markFlushStart: (batchUuids?: ReadonlySet<string>) => void;
  recordSuccess: (appliedUuids: ReadonlySet<string>) => void;
  recordError: () => void;
  /** Drop items whose failure can never resolve, unblocking the rest
   * of the queue. Clears the error budget so what remains flushes at
   * the fast cadence again. */
  discard: (uuids: ReadonlySet<string>) => void;
  resetErrorBudget: () => void;
};

export const createBuilderActionsSyncStore = <TItem extends { uuid: string }>(
  shouldDedupe: (existing: TItem, next: TItem) => boolean,
): UseBoundStore<StoreApi<BuilderActionsSyncStore<TItem>>> =>
  create<BuilderActionsSyncStore<TItem>>((set) => ({
    queue: [],
    lastSyncAt: new Date(0),
    firstSyncErrorAt: null,
    lastSuccessfulSyncAt: null,
    inFlightUuids: new Set(),
    inFlightSince: null,
    enqueue: (item) =>
      set((s) => ({
        queue: enqueueWithDedupe(s.queue, item, shouldDedupe),
      })),
    markFlushStart: (batchUuids) => {
      const now = new Date();
      set({
        lastSyncAt: now,
        inFlightSince: now,
        inFlightUuids: batchUuids ?? new Set(),
      });
    },
    recordSuccess: (appliedUuids) =>
      set((s) => ({
        queue:
          appliedUuids.size === 0
            ? s.queue
            : evictApplied(s.queue, appliedUuids),
        lastSuccessfulSyncAt: new Date(),
        firstSyncErrorAt: null,
        inFlightSince: null,
      })),
    recordError: () =>
      set((s) => ({
        lastSyncAt: new Date(),
        firstSyncErrorAt: s.firstSyncErrorAt ?? new Date(),
        inFlightSince: null,
      })),
    discard: (uuids) =>
      set((s) => ({
        queue: uuids.size === 0 ? s.queue : evictApplied(s.queue, uuids),
        firstSyncErrorAt: null,
      })),
    resetErrorBudget: () => set({ firstSyncErrorAt: null }),
  }));

// ─── Clean wait ──────────────────────────────────────────────────────────

/**
 * Calls `onClean` once the store holds no queued or in-flight edits (the
 * queued ones `isMine` picks, when given): at once if it already does, else
 * on the first store change that leaves it so. Returns the unsubscribe, for
 * an unmount that comes first.
 */
export const whenSyncQueueIsClean = <TItem extends { uuid: string }>(
  store: UseBoundStore<StoreApi<BuilderActionsSyncStore<TItem>>>,
  onClean: () => void,
  isMine: (item: TItem) => boolean = () => true,
): (() => void) => {
  const isClean = (s: BuilderActionsSyncStore<TItem>): boolean =>
    s.inFlightSince === null && !s.queue.some(isMine);
  if (isClean(store.getState())) {
    onClean();
    return () => {};
  }
  const unsubscribe = store.subscribe((s) => {
    if (!isClean(s)) return;
    unsubscribe();
    onClean();
  });
  return unsubscribe;
};

// ─── Enqueue wrapper hook ───────────────────────────────────────────────

/**
 * Builds the standard "wrap input with uuid + clientId, push to store"
 * hook. LibraryItem's `BuilderActionPartial` slots directly into
 * `Omit<TItem, "uuid" | "clientId">`. Workflow's input is just
 * `payload`, so its consumer keeps a one-line wrapper to nest it.
 */
export const useEnqueueBuilderAction = <
  TItem extends { uuid: string; clientId: string },
>(
  store: UseBoundStore<StoreApi<BuilderActionsSyncStore<TItem>>>,
): ((item: Omit<TItem, "uuid" | "clientId">) => void) => {
  const clientIdContext = useContext(ClientIdContext);
  const enqueueOnStore = store((s) => s.enqueue);
  return useCallback(
    (item) => {
      if (!clientIdContext) return;
      enqueueOnStore({
        uuid: uuid4(),
        clientId: clientIdContext.clientId,
        ...item,
      } as TItem);
    },
    [clientIdContext, enqueueOnStore],
  );
};

// ─── Flush driver ───────────────────────────────────────────────────────

type FlushDriverArgs<TItem extends { uuid: string }> = {
  store: UseBoundStore<StoreApi<BuilderActionsSyncStore<TItem>>>;
  // Apollo's mutate signature, so consumers can pass the generated
  // hook's mutate function directly without wrapping.
  mutate: (opts: { variables: { actions: BuilderActionInput[] } }) => unknown;
  loading: boolean;
  transform: (item: TItem) => BuilderActionInput;
};

/**
 * Owns the flush lifecycle for a BuilderActions sync queue:
 * - Reactive flush whenever the queue or `loading` flips.
 * - Periodic retry every second (post-error retry path).
 * - Resets `firstSyncErrorAt` on mount so a poisoned budget from a
 *   prior session doesn't gate every flush in this one.
 * - Best-effort final flush on unmount. Apollo keeps the mutation
 *   in-flight after unmount; `onCompleted` won't fire so the queue
 *   isn't evicted, but the server's uuid idempotency makes a
 *   duplicate send on next mount a no-op.
 *
 * Mount at a level that outlives every component that can enqueue.
 */
export const useBuilderActionsFlushDriver = <TItem extends { uuid: string }>({
  store,
  mutate,
  loading,
  transform,
}: FlushDriverArgs<TItem>): void => {
  const markFlushStart = store((s) => s.markFlushStart);
  const resetErrorBudget = store((s) => s.resetErrorBudget);

  const flush = useCallback(() => {
    const { queue, lastSyncAt, firstSyncErrorAt } = store.getState();
    if (queue.length === 0) return;
    if (loading && secondsSinceDate(lastSyncAt) < STUCK_INFLIGHT_SECONDS)
      return;
    if (secondsSinceDate(lastSyncAt) <= DEBOUNCE_SECONDS) return;
    if (!shouldRetryFailedSync(firstSyncErrorAt, lastSyncAt)) return;
    const batch = queue.slice(0, MAX_PER_FLUSH);
    markFlushStart(new Set(batch.map((item) => item.uuid)));
    mutate({
      variables: { actions: batch.map(transform) },
    });
  }, [store, mutate, loading, markFlushStart, transform]);

  useEffect(() => {
    resetErrorBudget();
    // A flush sent by a previous mount never records its response
    // (onCompleted doesn't fire after unmount), so don't report it as
    // still in flight.
    store.setState({ inFlightSince: null });
  }, [resetErrorBudget, store]);

  // Fire immediately when `flush` identity changes (loading flipped,
  // queue grew, etc.) and keep retrying once per second to cover the
  // post-error case where the reactive trigger doesn't fire again.
  useEffect(() => {
    flush();
    const interval = setInterval(flush, 1000);
    return () => clearInterval(interval);
  }, [flush]);

  useEffect(() => {
    return () => {
      const state = store.getState();
      if (state.queue.length === 0) return;
      const batch = state.queue.slice(0, MAX_PER_FLUSH);
      state.markFlushStart(new Set(batch.map((item) => item.uuid)));
      mutate({
        variables: { actions: batch.map(transform) },
      });
    };
  }, [store, mutate, transform]);

  // The queue lives only in memory: navigating away or reloading with
  // queued edits discards them, so make the browser ask first.
  useEffect(() => {
    const warnOnUnloadWithUnsaved = (e: BeforeUnloadEvent) => {
      if (store.getState().queue.length === 0) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warnOnUnloadWithUnsaved);
    return () =>
      window.removeEventListener("beforeunload", warnOnUnloadWithUnsaved);
  }, [store]);
};
